import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  FaArrowLeft,
  FaSearch,
  FaSpinner,
  FaPrint,
  FaPen,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/axios";
import useAuth from "../../hooks/useAuthContext";
import EtiquetaCracha from "./EtiquetaCracha";
import ModalEtiquetaManual from "./ModalEtiquetaManual";
import "./admPatrocinadoresList.css";
import "./admEtiquetas.css";

const LABEL_TIPO = {
  convidado: "Convidado",
  participante_patrocinador: "Patrocinador",
};

const OPCOES_FILTRO_TIPO = [
  { value: "", label: "Todos os tipos" },
  { value: "convidado", label: LABEL_TIPO.convidado },
  {
    value: "participante_patrocinador",
    label: LABEL_TIPO.participante_patrocinador,
  },
];

const nomeExibicao = (u) =>
  (u.nome_cracha || u.nomeCracha || "").trim();

const montarEtiquetaDeUsuario = (u) => ({
  key: `u-${u.id}`,
  nome: nomeExibicao(u),
  cargo: u.cargo || "",
  empresa: u.empresa || "",
  id: u.id,
  tipo: u.tipo,
  mostrarQr: u.tipo === "convidado",
});

const AdmEtiquetas = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [etiquetasParaImprimir, setEtiquetasParaImprimir] = useState([]);
  const printPendingRef = useRef(false);

  useEffect(() => {
    if (usuario && usuario.tipo !== "administracao") {
      navigate("/private/adm");
    }
  }, [usuario, navigate]);

  const carregarUsuarios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.post(`/usuarios-apenas-user`, {
        tipo: ["convidado", "participante_patrocinador"],
      });
      if (resp.status === 200) {
        setUsuarios(resp.data.dados || []);
      }
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
      setError("Erro ao carregar lista de convidados e participantes");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (usuario?.tipo === "administracao") {
      carregarUsuarios();
    }
  }, [usuario?.tipo, carregarUsuarios]);

  const usuariosFiltrados = useMemo(() => {
    const nome = filtroNome.toLowerCase().trim();
    const empresa = filtroEmpresa.toLowerCase().trim();

    return usuarios.filter((u) => {
      const nomeMatch =
        !nome ||
        [u.nome_cracha, u.nomeCracha]
          .filter(Boolean)
          .some((v) => v.toString().toLowerCase().includes(nome));

      const empresaMatch =
        !empresa ||
        (u.empresa && u.empresa.toString().toLowerCase().includes(empresa));

      const tipoMatch = !filtroTipo || u.tipo === filtroTipo;

      return nomeMatch && empresaMatch && tipoMatch;
    });
  }, [usuarios, filtroNome, filtroEmpresa, filtroTipo]);

  const idsFiltrados = useMemo(
    () => usuariosFiltrados.map((u) => u.id),
    [usuariosFiltrados],
  );

  const todosSelecionados =
    idsFiltrados.length > 0 &&
    idsFiltrados.every((id) => selecionados.has(id));

  const toggleTodos = useCallback(() => {
    setSelecionados((prev) => {
      if (idsFiltrados.every((id) => prev.has(id))) {
        const next = new Set(prev);
        idsFiltrados.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      idsFiltrados.forEach((id) => next.add(id));
      return next;
    });
  }, [idsFiltrados]);

  const toggleUm = useCallback((id) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const imprimirEtiquetas = useCallback((lista) => {
    if (!lista || lista.length === 0) {
      toast.warn("Nenhuma etiqueta para imprimir.");
      return;
    }
    printPendingRef.current = true;
    setEtiquetasParaImprimir(lista);
  }, []);

  useEffect(() => {
    if (!printPendingRef.current || etiquetasParaImprimir.length === 0) {
      return undefined;
    }

    const styleId = "aet-print-page-style";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      styleEl.textContent = "@page { size: 95mm 40mm; margin: 0; }";
      document.head.appendChild(styleEl);
    }

    document.body.classList.add("aet-printing");

    const limparAposPrint = () => {
      document.body.classList.remove("aet-printing");
      setEtiquetasParaImprimir([]);
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };

    const timer = window.setTimeout(() => {
      printPendingRef.current = false;
      window.print();
    }, 300);

    window.addEventListener("afterprint", limparAposPrint);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", limparAposPrint);
      document.body.classList.remove("aet-printing");
    };
  }, [etiquetasParaImprimir]);

  const handleImprimirUnitario = useCallback(
    (user) => {
      imprimirEtiquetas([montarEtiquetaDeUsuario(user)]);
    },
    [imprimirEtiquetas],
  );

  const handleImprimirSelecionados = useCallback(() => {
    const lista = usuarios
      .filter((u) => selecionados.has(u.id))
      .map(montarEtiquetaDeUsuario);

    if (lista.length === 0) {
      toast.warn("Selecione ao menos um registro.");
      return;
    }
    imprimirEtiquetas(lista);
  }, [usuarios, selecionados, imprimirEtiquetas]);

  const handleImprimirManual = useCallback(
    (dados) => {
      imprimirEtiquetas([
        {
          key: `manual-${Date.now()}`,
          nome: dados.nome,
          cargo: dados.cargo,
          empresa: dados.empresa,
          mostrarQr: false,
        },
      ]);
    },
    [imprimirEtiquetas],
  );

  if (usuario?.tipo !== "administracao") {
    return null;
  }

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho de etiquetas">
          <div className="apl-header-text">
            <h1>Etiquetas de crachá</h1>
            <p>
              Imprima etiquetas 95×40 mm para convidados e participantes de
              patrocinadores. QR Code apenas para convidados.
            </p>
          </div>

          <div className="apl-toolbar">
            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={() => setModalAberto(true)}
              disabled={isLoading}
            >
              <FaPen size={14} aria-hidden />
              Etiqueta manual
            </button>

            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={handleImprimirSelecionados}
              disabled={isLoading || selecionados.size === 0}
              title="Imprimir selecionados"
            >
              <FaPrint size={14} aria-hidden />
              Imprimir selecionados
              {selecionados.size > 0 ? ` (${selecionados.size})` : ""}
            </button>

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          <div className="aet-filters">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Filtrar por nome crachá…"
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                disabled={isLoading}
                aria-label="Filtrar por nome crachá"
              />
            </div>
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Filtrar por empresa…"
                value={filtroEmpresa}
                onChange={(e) => setFiltroEmpresa(e.target.value)}
                disabled={isLoading}
                aria-label="Filtrar por empresa"
              />
            </div>
            <label className="apl-filter-pill apl-filter-pill--select">
              <span>Tipo</span>
              <select
                className="apl-filter-select"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                disabled={isLoading}
                aria-label="Filtrar por tipo"
              >
                {OPCOES_FILTRO_TIPO.map((opcao) => (
                  <option key={opcao.value || "todos"} value={opcao.value}>
                    {opcao.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando…</span>
              </div>
            </div>
          ) : (
            <section className="apl-card" aria-label="Lista para etiquetas">
              <div className="apl-table-wrap">
                {usuariosFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {usuarios.length === 0
                        ? "Nenhum registro encontrado"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {usuarios.length === 0
                        ? "Não há convidados ou participantes cadastrados."
                        : "Ajuste os filtros de nome, empresa ou tipo."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th className="aet-check-col" scope="col">
                          <input
                            type="checkbox"
                            checked={todosSelecionados}
                            onChange={toggleTodos}
                            aria-label="Selecionar todos filtrados"
                          />
                        </th>
                        <th scope="col">Nome crachá</th>
                        <th scope="col">Cargo</th>
                        <th scope="col">Empresa</th>
                        <th scope="col">Tipo</th>
                        <th className="aet-actions-col" scope="col">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosFiltrados.map((u) => (
                        <tr key={u.id}>
                          <td className="aet-check-col">
                            <input
                              type="checkbox"
                              checked={selecionados.has(u.id)}
                              onChange={() => toggleUm(u.id)}
                              aria-label={`Selecionar ${nomeExibicao(u)}`}
                            />
                          </td>
                          <td>{nomeExibicao(u) || "—"}</td>
                          <td>{u.cargo || "—"}</td>
                          <td>{u.empresa || "—"}</td>
                          <td>
                            <span
                              className={`aet-tipo-badge ${
                                u.tipo === "convidado"
                                  ? "aet-tipo-badge--convidado"
                                  : "aet-tipo-badge--participante"
                              }`}
                            >
                              {LABEL_TIPO[u.tipo] || u.tipo}
                            </span>
                          </td>
                          <td className="aet-actions-col">
                            <button
                              type="button"
                              className="aet-btn-icon"
                              onClick={() => handleImprimirUnitario(u)}
                              title="Imprimir etiqueta"
                            >
                              <FaPrint size={13} aria-hidden />
                              Imprimir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      <ModalEtiquetaManual
        show={modalAberto}
        onHide={() => setModalAberto(false)}
        onImprimir={handleImprimirManual}
      />

      {etiquetasParaImprimir.length > 0 ? (
        <div className="area-impressao-etiquetas" aria-hidden>
          {etiquetasParaImprimir.map((et) => (
            <EtiquetaCracha
              key={et.key}
              nome={et.nome}
              cargo={et.cargo}
              empresa={et.empresa}
              id={et.id}
              tipo={et.tipo}
              mostrarQr={et.mostrarQr}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AdmEtiquetas;
